import { Link } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { useSession } from '../context/SessionContext'
import { startDiscordLogin } from '../services/auth'
import { fetchMySchedule, fetchSeasons } from '../services/league'
import {
  Card,
  ConferenceBadge,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  SortableHeaderRow,
  Stat,
  formatRecord,
  formatSeasonStatus,
  playerProfilePath,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import { CopyDiscordTags } from '../components/CopyDiscordTags'
import type { ScheduleFixture } from '../types'

/**
 * @remarks
 * The round robin has no fixed week order — the server returns whoever is left
 * to play alongside whoever has been played — so there is no schedule sequence
 * for sorting to destroy. `Status` sorts unplayed fixtures to one end, which is
 * the question this page exists to answer, and `Score` sorts on the margin from
 * the signed-in player's side, matching the way the cell already reads. An
 * unplayed fixture has no margin, so it groups at the end in both directions.
 */
const FIXTURE_COLUMNS: Array<SortableColumn<ScheduleFixture>> = [
  { key: 'opponent', label: 'Opponent', sort: fixture => fixture.opponentDisplayName },
  {
    key: 'status',
    label: 'Status',
    hint: 'Sort played fixtures apart from upcoming ones',
    sort: fixture => (fixture.played ? `1 ${fixture.result ?? 'played'}` : '0 upcoming'),
  },
  {
    key: 'score',
    label: 'Score',
    className: 'num',
    hint: 'Sort by margin',
    sortLabel: 'margin',
    sort: fixture =>
      fixture.played && fixture.pointsFor !== null && fixture.pointsAgainst !== null
        ? fixture.pointsFor - fixture.pointsAgainst
        : null,
  },
  { key: 'actions' },
]

/**
 * One row of the round robin.
 *
 * @remarks
 * Score is shown from the signed-in player's perspective, so it reads the same
 * way as the result badge beside it rather than needing home/away context.
 */
function FixtureRow({ fixture }: { fixture: ScheduleFixture }) {
  return (
    <tr>
      <td>
        <PlayerCell
          name={fixture.opponentDisplayName}
          discordId={fixture.opponentDiscordId}
          avatar={fixture.opponentDiscordAvatar}
          to={playerProfilePath(fixture.opponentLeaguePlayerId)}
        />
      </td>
      <td>
        {fixture.played ? (
          <span className={`badge badge--${fixture.result ?? 'pending'}`}>
            {fixture.result === 'win' ? 'Win' : fixture.result === 'loss' ? 'Loss' : 'Played'}
          </span>
        ) : (
          <span className="badge">Upcoming</span>
        )}
      </td>
      <td className="num">
        {fixture.played ? (
          `${fixture.pointsFor}–${fixture.pointsAgainst}`
        ) : (
          <span className="muted">Not played yet</span>
        )}
      </td>
      <td className="num">
        {fixture.gameId && (
          <Link className="btn btn--ghost btn--sm" to={`/games/${fixture.gameId}`}>
            Box score
          </Link>
        )}
      </td>
    </tr>
  )
}

function FixtureTable({ fixtures }: { fixtures: ScheduleFixture[] }) {
  const sort = useTableSort(fixtures, FIXTURE_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={FIXTURE_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(fixture => (
            <FixtureRow key={fixture.opponentLeaguePlayerId} fixture={fixture} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function MySchedule() {
  const { user, loading: sessionLoading } = useSession()
  const seasons = useAsync(fetchSeasons, [])
  const currentSeason = seasons.data?.find(season => season.isCurrent)
  const seasonNumber = currentSeason?.number

  const schedule = useAsync(
    () => (user ? fetchMySchedule(seasonNumber) : Promise.resolve(null)),
    [user?.id, seasonNumber]
  )

  const fixtures = schedule.data?.fixtures ?? []
  const played = fixtures.filter(fixture => fixture.played)
  const wins = played.filter(fixture => fixture.result === 'win').length
  const losses = played.filter(fixture => fixture.result === 'loss').length
  const remaining = fixtures.length - played.length

  const header = (
    <header className="page-header">
      <div>
        <span className="eyebrow">My Schedule</span>
        <h1>
          {currentSeason
            ? `Season ${currentSeason.number} · ${formatSeasonStatus(currentSeason.status)}`
            : 'My Schedule'}
        </h1>
        <p>
          The league plays an intra-conference round robin, so your schedule is everyone else
          in your conference — once each.
        </p>
      </div>
      {schedule.data?.conference && <ConferenceBadge conference={schedule.data.conference} />}
    </header>
  )

  if (sessionLoading) {
    return (
      <div className="stack">
        {header}
        <Card>
          <Loading rows={3} />
        </Card>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="stack">
        {header}
        <Card>
          <EmptyState title="Sign in to see your schedule">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => startDiscordLogin()}
              style={{ marginTop: 12 }}
            >
              Sign in with Discord
            </button>
          </EmptyState>
        </Card>
      </div>
    )
  }

  return (
    <div className="stack">
      {header}

      {schedule.error && <Notice tone="error">{schedule.error}</Notice>}

      {schedule.loading ? (
        <Card>
          <Loading rows={4} />
        </Card>
      ) : schedule.data?.unavailableReason ? (
        <Card>
          <EmptyState title="No schedule this season">
            {schedule.data.unavailableReason} An admin assigns conference rosters from the
            Admin page.
          </EmptyState>
        </Card>
      ) : (
        <>
          <div className="grid-3">
            <Stat label="Record" value={formatRecord(wins, losses, 0)} hint="Official games" />
            <Stat label="Games played" value={played.length} hint={`of ${fixtures.length}`} />
            <Stat
              label="Remaining"
              value={remaining}
              hint={remaining === 0 ? 'Round robin complete' : 'Still to arrange'}
            />
          </div>

          {/* A conference of one has nothing to tag; the table's empty state says so. */}
          {fixtures.length > 0 && (
            <div className="stack-sm">
              <CopyDiscordTags schedule={schedule.data} loading={schedule.loading} />
            </div>
          )}

          <Card title="Opponents" subtitle={`${fixtures.length} in your conference`} flush>
            {fixtures.length === 0 ? (
              <EmptyState title="No opponents yet">
                You are the only player in your conference so far.
              </EmptyState>
            ) : (
              <FixtureTable fixtures={fixtures} />
            )}
          </Card>
        </>
      )}
    </div>
  )
}
